import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ShoppingBag, 
  Store, 
  Truck, 
  Clock, 
  MapPin, 
  CreditCard, 
  ShieldCheck, 
  CheckCircle2, 
  Sparkles, 
  Smartphone, 
  ExternalLink, 
  Copy, 
  Trash2, 
  RotateCcw, 
  X, 
  Bot, 
  Phone, 
  User, 
  FileText,
  Gift
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Language } from '../types';
import { toast } from 'sonner';
import { openStoreBrandedAppOrWeb } from '../lib/storeAppOpener';

export interface NutriAgentOrderRecord {
  id: string;
  store: string;
  rawStore?: string;
  total: number;
  subtotal?: number;
  deliveryFee?: number;
  discount?: number;
  items: number;
  itemDetails?: Array<{
    name: string;
    qty: number;
    price: number;
    icon?: string;
    productUrl?: string;
    macros?: { protein?: number; carbs?: number; fat?: number };
    productType?: 'weight' | 'pack';
    unit?: string;
    weightGrams?: number;
  }>;
  deliveryAddress?: string;
  deliveryNotes?: string;
  deliverySlot?: string;
  recipientName?: string;
  recipientPhone?: string;
  paymentMethod?: string;
  timestamp: number;
  currencySymbol?: string;
  currency?: string;
  status?: string;
  checkoutUrl?: string;
  isExpress?: boolean;
}

interface NutriAgentOrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: NutriAgentOrderRecord | null;
  lang: Language;
  theme?: 'light' | 'dark';
  onDelete?: (orderId: string) => void;
  onReorder?: (order: NutriAgentOrderRecord) => void;
}

export const NutriAgentOrderDetailModal: React.FC<NutriAgentOrderDetailModalProps> = ({
  isOpen,
  onClose,
  order,
  lang,
  theme = 'dark',
  onDelete,
  onReorder
}) => {
  if (!isOpen || !order) return null;

  const isUa = lang === 'ua';
  const currencySymbol = order.currencySymbol || (order.currency === 'UAH' ? '₴' : (isUa ? '₴' : '$'));
  const storeKey = (order.rawStore || order.store || 'silpo').toLowerCase();
  const isPickup = (order.deliveryAddress && /самовивіз|pickup/i.test(order.deliveryAddress)) || (order.deliveryFee === 0 && !order.deliveryAddress);

  const formattedDate = new Date(order.timestamp).toLocaleString(
    isUa ? 'uk-UA' : 'en-US',
    {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
  );

  const totalProtein = (order.itemDetails || []).reduce((acc, it) => acc + ((it.macros?.protein || 0) * (it.qty || 1)), 0);
  const totalCalories = (order.itemDetails || []).reduce((acc, it) => {
    const p = it.macros?.protein || 0;
    const c = it.macros?.carbs || 0;
    const f = it.macros?.fat || 0;
    return acc + ((p * 4) + (c * 4) + (f * 9)) * (it.qty || 1);
  }, 0);

  const handleOpenStore = (forceWeb?: boolean) => {
    openStoreBrandedAppOrWeb({
      store: storeKey,
      webUrl: order.checkoutUrl || (storeKey === 'silpo' ? 'https://silpo.ua/checkout' : 'https://www.atbmarket.com/cart'),
      action: 'checkout',
      forceWeb: forceWeb
    });
  };

  const handleCopySummary = () => {
    const header = isUa 
      ? `🛒 Замовлення Nutri-Agent (${order.store}) — #${order.id}\n📅 Дата: ${formattedDate}\n` 
      : `🛒 Nutri-Agent Order (${order.store}) — #${order.id}\n📅 Date: ${formattedDate}\n`;
    
    const lines = (order.itemDetails || []).map((it, idx) => 
      `${idx + 1}. ${it.name} (x${it.qty}) — ${currencySymbol}${(it.price * it.qty).toFixed(2)}`
    ).join('\n');

    const totalText = isUa
      ? `\n💰 Разом сплачено: ${currencySymbol}${order.total?.toFixed(2)}\n📍 Доставка: ${order.deliveryAddress || 'Вказана адреса'}\n⏰ Час: ${order.deliverySlot || 'Найближчий час'}`
      : `\n💰 Total Paid: ${currencySymbol}${order.total?.toFixed(2)}\n📍 Destination: ${order.deliveryAddress || 'Specified address'}\n⏰ Time: ${order.deliverySlot || 'Standard slot'}`;

    navigator.clipboard.writeText(`${header}\n${lines}\n${totalText}`).then(() => {
      toast.success(isUa ? 'Деталі замовлення скопійовано!' : 'Order details copied to clipboard!', { icon: '📋' });
    });
  };

  const handleDelete = () => {
    if (onDelete && order.id) {
      onDelete(order.id);
      toast.success(isUa ? 'Замовлення видалено з історії' : 'Order deleted from history');
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 350, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "relative w-full max-w-xl overflow-hidden rounded-3xl border shadow-2xl flex flex-col max-h-[90vh]",
            "bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 border-zinc-200 dark:border-zinc-800"
          )}
        >
          {/* Header */}
          <div className="p-5 pb-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between shrink-0 bg-white dark:bg-zinc-950">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500 shrink-0">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">
                    {isUa ? 'Деталі замовлення' : 'Order Details & Flow'}
                  </h2>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                    {order.store}
                  </span>
                </div>
                <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500">
                  Ref: {order.id} • {formattedDate}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="p-5 space-y-5 overflow-y-auto no-scrollbar flex-1">
            {/* Status & Summary Banner */}
            <div className="bg-emerald-500/10 border border-emerald-500/25 p-4 rounded-2xl flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-xs shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-xs sm:text-sm text-emerald-800 dark:text-emerald-300">
                    {isUa ? 'Успішно оформлено та передано' : 'Successfully Placed & Dispatched'}
                  </h3>
                  <p className="text-[11px] text-zinc-600 dark:text-zinc-400">
                    {isUa 
                      ? `${order.items} позицій • Загалом ${currencySymbol}${order.total?.toFixed(2)}` 
                      : `${order.items} items • Total ${currencySymbol}${order.total?.toFixed(2)}`}
                  </p>
                </div>
              </div>

              <div className="text-right shrink-0">
                <span className="text-xs font-mono text-zinc-400 block">{isUa ? 'Статус' : 'Status'}</span>
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  {order.status === 'PAID_AND_DISPATCHED' 
                    ? (isUa ? 'Оплачено' : 'Paid') 
                    : (order.status || (isUa ? 'Готово' : 'Ready'))}
                </span>
              </div>
            </div>

            {/* Nutri-Agent Execution Flow (Timeline) */}
            <div className="bg-zinc-50 dark:bg-zinc-900/50 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wide flex items-center gap-1.5">
                  <Bot className="w-3.5 h-3.5 text-emerald-500" />
                  {isUa ? 'Хід виконання Nutri-Agent' : 'Nutri-Agent Execution Flow'}
                </span>
                <span className="text-[9px] font-mono font-bold text-emerald-600 dark:text-emerald-400">
                  100% {isUa ? 'Автономно' : 'Autonomous'}
                </span>
              </div>

              <div className="space-y-2.5 pl-2 border-l-2 border-emerald-500/40 ml-1.5">
                <div className="relative pl-3">
                  <div className="absolute -left-[11px] top-1 w-2 h-2 rounded-full bg-emerald-500" />
                  <p className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200">
                    {isUa ? '1. Аналіз дефіцитів та SafeChoice' : '1. Deficit Analysis & SafeChoice Validation'}
                  </p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                    {isUa 
                      ? `Перевірено відсутність алергенів. Набрано ~${totalProtein}g чистого білка.` 
                      : `Zero allergens flagged. Achieved ~${totalProtein}g clean protein.`}
                  </p>
                </div>

                <div className="relative pl-3">
                  <div className="absolute -left-[11px] top-1 w-2 h-2 rounded-full bg-emerald-500" />
                  <p className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200">
                    {isUa ? `2. Оптимізація цін у ${order.store}` : `2. Catalog Optimization in ${order.store}`}
                  </p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                    {isUa 
                      ? `Знайдено актуальні залишки та найкращі пропозиції за бюджетом.` 
                      : `Matched in-stock inventory within target budget ceiling.`}
                  </p>
                </div>

                <div className="relative pl-3">
                  <div className="absolute -left-[11px] top-1 w-2 h-2 rounded-full bg-emerald-500" />
                  <p className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200">
                    {isUa ? '3. Синхронізація кошика та знижок' : '3. Cart Handshake & Loyalty Sync'}
                  </p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                    {isUa 
                      ? `Зв'язано акаунт, застосовано балабонуси та підтверджено часовий слот.` 
                      : `Connected store account, applied loyalty perks & verified delivery timeslot.`}
                  </p>
                </div>

                <div className="relative pl-3">
                  <div className="absolute -left-[11px] top-1 w-2 h-2 rounded-full bg-emerald-500" />
                  <p className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200">
                    {isUa ? '4. Перехід до оплати та викуп' : '4. Checkout Transfer & Completion'}
                  </p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                    {isUa 
                      ? 'Офіційний шлюз підтверджено, товари збережено в історії додатку.' 
                      : 'Merchant transaction validated, order archived to your history log.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Purchased Items List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wide flex items-center gap-1.5">
                  <ShoppingBag className="w-3.5 h-3.5 text-emerald-500" />
                  {isUa ? `Придбані товари (${order.itemDetails?.length || order.items})` : `Purchased Items (${order.itemDetails?.length || order.items})`}
                </span>
                {totalProtein > 0 && (
                  <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                    +{totalProtein}g {isUa ? 'білка' : 'protein'} {totalCalories > 0 ? `• ~${totalCalories} ккал` : ''}
                  </span>
                )}
              </div>

              <div className="space-y-2 max-h-[240px] overflow-y-auto no-scrollbar pr-1">
                {(order.itemDetails && order.itemDetails.length > 0) ? (
                  order.itemDetails.map((it, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 text-xs"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <span className="text-lg select-none shrink-0">{it.icon || '🛒'}</span>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">{it.name}</p>
                          <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                            <span className="font-bold text-zinc-800 dark:text-zinc-200 bg-zinc-200/60 dark:bg-zinc-800 px-1.5 py-0.2 rounded">
                              {it.qty} {isUa ? 'шт' : 'x'}
                            </span>
                            <span>•</span>
                            <span>{currencySymbol}{it.price?.toFixed(2)}</span>
                            {it.macros?.protein ? (
                              <>
                                <span>•</span>
                                <span className="text-emerald-600 dark:text-emerald-400 font-medium">+{it.macros.protein * it.qty}g {isUa ? 'білка' : 'protein'}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="text-right shrink-0 pl-2">
                        <span className="font-bold text-zinc-900 dark:text-zinc-100 text-xs block">
                          {currencySymbol}{((it.price || 0) * (it.qty || 1)).toFixed(2)}
                        </span>
                        {it.productUrl && (
                          <button
                            type="button"
                            onClick={() => openStoreBrandedAppOrWeb({
                              store: storeKey,
                              webUrl: it.productUrl,
                              action: 'product'
                            })}
                            className="text-[9px] text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-0.5 cursor-pointer"
                          >
                            <span>{isUa ? 'У магазині' : 'View'}</span>
                            <ExternalLink className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800 text-center text-xs text-zinc-500">
                    {isUa ? `Сформовано кошик на ${order.items} товарів` : `Cart with ${order.items} grocery items`}
                  </div>
                )}
              </div>
            </div>

            {/* Delivery & Recipient Details */}
            <div className="bg-zinc-50 dark:bg-zinc-900/50 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 space-y-2.5 text-xs text-zinc-700 dark:text-zinc-300">
              <span className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider block">
                {isUa ? 'Параметри отримання' : 'Fulfillment Details'}
              </span>

              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {isPickup ? <Store className="w-4 h-4 text-emerald-500 shrink-0" /> : <Truck className="w-4 h-4 text-emerald-500 shrink-0" />}
                  <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                    {isPickup 
                      ? (isUa ? 'Самовивіз із магазину' : 'Store Pickup') 
                      : (isUa ? 'Курʼєрська доставка' : 'Courier Delivery')}
                    {order.isExpress && <span className="ml-1.5 text-[10px] text-amber-500 font-bold">⚡ Експрес</span>}
                  </span>
                </div>
                <span className="text-[11px] text-zinc-500 font-medium">{order.deliverySlot || (isUa ? 'Найближчий час' : 'Standard slot')}</span>
              </div>

              {order.deliveryAddress && (
                <div className="flex items-start gap-2 pt-1 border-t border-zinc-200 dark:border-zinc-800/60">
                  <MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">{order.deliveryAddress}</p>
                    {order.deliveryNotes && (
                      <p className="text-[10px] text-zinc-400 italic mt-0.5">{order.deliveryNotes}</p>
                    )}
                  </div>
                </div>
              )}

              {(order.recipientPhone || order.recipientName) && (
                <div className="flex items-center justify-between text-[11px] pt-1 border-t border-zinc-200 dark:border-zinc-800/60 text-zinc-600 dark:text-zinc-400">
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-emerald-500" />
                    <span>{order.recipientName || (isUa ? 'Отримувач' : 'Recipient')}</span>
                  </span>
                  {order.recipientPhone && (
                    <span className="flex items-center gap-1 font-mono">
                      <Phone className="w-3.5 h-3.5 text-emerald-500" />
                      <span>{order.recipientPhone}</span>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Financial Summary Breakdown */}
            <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 space-y-2 text-xs">
              {order.subtotal !== undefined && (
                <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                  <span>{isUa ? 'Сума товарів:' : 'Subtotal:'}</span>
                  <span className="font-semibold text-zinc-800 dark:text-zinc-200">{currencySymbol}{order.subtotal.toFixed(2)}</span>
                </div>
              )}
              {order.deliveryFee !== undefined && (
                <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                  <span>{isPickup ? (isUa ? 'Самовивіз:' : 'Pickup:') : (isUa ? 'Доставка:' : 'Delivery:')}</span>
                  <span className="font-semibold text-zinc-800 dark:text-zinc-200">
                    {order.deliveryFee === 0 ? (isUa ? 'Безкоштовно (0 ₴)' : 'Free') : `${currencySymbol}${order.deliveryFee.toFixed(2)}`}
                  </span>
                </div>
              )}
              {order.discount !== undefined && order.discount > 0 && (
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                  <span className="flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    <span>{isUa ? 'Знижка / Балабонуси:' : 'Loyalty Discount:'}</span>
                  </span>
                  <span>-{currencySymbol}{order.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800 flex justify-between text-sm font-bold text-zinc-900 dark:text-zinc-50">
                <span>{isUa ? 'Разом сплачено:' : 'Total Paid:'}</span>
                <span className="text-emerald-600 dark:text-emerald-400 text-base font-black">
                  {currencySymbol}{order.total?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Action Footer Buttons */}
          <div className="p-5 pt-3.5 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex flex-col gap-2 shrink-0">
            {storeKey === 'silpo' ? (
              <>
                <button
                  type="button"
                  onClick={() => handleOpenStore(false)}
                  className="w-full py-3.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-md shadow-emerald-500/20 uppercase tracking-wider"
                >
                  <Smartphone className="w-4 h-4" />
                  {isUa ? 'Відкрити в додатку Сільпо 📱' : 'Open in Silpo Mobile App 📱'}
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenStore(true)}
                  className="w-full py-2.5 px-4 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 font-semibold flex items-center justify-center gap-2 transition-all text-xs cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {isUa ? 'Відкрити на сайті silpo.ua 🌐' : 'Open on silpo.ua Website 🌐'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => handleOpenStore(false)}
                className="w-full py-3.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-md shadow-emerald-500/20 uppercase tracking-wider"
              >
                <Smartphone className="w-4 h-4" />
                {isUa 
                  ? `Відкрити в додатку ${order.store} 📱` 
                  : `Open in ${order.store} Mobile App 📱`}
              </button>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleCopySummary}
                className="py-2.5 px-3 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 transition-all cursor-pointer flex items-center justify-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>{isUa ? 'Скопіювати чек' : 'Copy Summary'}</span>
              </button>

              {onReorder && (
                <button
                  type="button"
                  onClick={() => {
                    onReorder(order);
                    onClose();
                  }}
                  className="py-2.5 px-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>{isUa ? 'Повторити' : 'Re-Order'}</span>
                </button>
              )}

              {!onReorder && onDelete && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="py-2.5 px-3 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/30 text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>{isUa ? 'Видалити' : 'Delete'}</span>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
